import type { History } from "history"
import React, { type PropsWithChildren } from "react"
import { useNavigate } from "react-router-dom"
import styled from "styled-components"
import { Icon, type IconProps } from "../Icon"
import { ZIndexRange } from "../types/enums"
import type { BaseProps } from "../types/props"
import { overloadColor } from "../util/style"
import { Button } from "./Button"

interface StyledButtonProps {
  top: string
  right: string
  bottom: string
  left: string
  zIndex: number
  size: string

  color: string
  hoverColor?: string
  background: string
  hoverBackground?: string
}
const StyledButton = styled(Button)<StyledButtonProps>`
  position: fixed;
  top: ${(props) => props.top};
  right: ${(props) => props.right};
  bottom: ${(props) => props.bottom};
  left: ${(props) => props.left};
  z-index: ${(props) => props.zIndex};

  display: flex;
  justify-content: center;
  align-items: center;

  width: ${(props) => props.size};
  height: ${(props) => props.size};
  border-radius: 50%;

  color: ${(props) => overloadColor(props.color)};
  background: ${(props) => overloadColor(props.background)};
  box-shadow: 1px 1px 3px 1px rgba(0, 0, 0, 0.2);

  transition: all 0.2s ease;
  i {
    transform: translateY(1px);
  }

  ${(props) => props.theme.media.desktop} {
    &:hover {
      transform: rotate(90deg);
      box-shadow: 1px 1px 6px 1px rgba(0, 0, 0, 0.3);

      i {
        transform: translateY(1px);
      }

      ${(props) =>
        props.hoverBackground &&
        `
        background: ${overloadColor(props.hoverBackground)};
      `};
    }
  }
`

export interface ActionButtonProps
  extends BaseProps,
    PropsWithChildren<object> {
  to?: History.LocationDescriptor

  top?: string
  right?: string
  bottom?: string
  left?: string
  zIndex?: number
  size?: string

  iconProps?: Partial<IconProps>
  noIcon?: boolean
  color?: string
  hoverColor?: string
  background?: string
  hoverBackground?: string

  onClick?: (evt: React.MouseEvent) => void
}

export const ActionButton = React.forwardRef<
  HTMLButtonElement,
  ActionButtonProps
>(
  (
    {
      children,
      className,

      to,
      role,

      top = "auto",
      right = "auto",
      bottom = "auto",
      left = "auto",
      zIndex = ZIndexRange.Loaders,
      size = "48px",

      iconProps = {},
      noIcon = false,
      color = "white",
      hoverColor,
      background = "primary",
      hoverBackground,

      onClick,
    },
    ref,
  ) => {
    const navigate = useNavigate()

    const overloadedIconProps: IconProps = {
      icon: "plus",
      ...iconProps,
    }

    function handleClick(evt: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
      onClick?.(evt)
      if (evt.defaultPrevented || !to) return
      navigate(to)
    }

    return (
      <StyledButton
        ref={ref}
        className={className}
        role={role ?? (to ? "link" : undefined)}
        top={top}
        right={right}
        bottom={bottom}
        left={left}
        zIndex={zIndex}
        size={size}
        color={color}
        hoverColor={hoverColor}
        background={background}
        hoverBackground={hoverBackground}
        onClick={handleClick}
      >
        {!noIcon && <Icon {...overloadedIconProps} />}
        {children}
      </StyledButton>
    )
  },
)
